<!--
  A sub component of FollowIntentEditor

  Props:
  - value: the value from the comparator
  - other: the other from the comparator
  - compare: comapre from the comparator
  - action: the follow action
  - prop: the prop prefix for el-form-item
  - active: -if should open the slidedowns
  - skillOptions:- the available public skills
  - intentOptions: the available intents, by skill
  - localVariables: for the code-input
  - contextVariables: for the code-input
  - variableSuggestions: for the code-input
  - intent: synced variable from parent
  - follow: synced variable from parent
  - variables: synced variable from parent
  - callback: synced variable from parent
  - callbackCopy: synced variable from parent
  - callbackVariables: synced variable from parent
  - speech: synced variable from parent
-->

<template>
  <div>
    <!-- speech inputs -->
    <el-collapsable v-if="action === null"
      v-model="advanced">
      <el-collapse-transition>
        <div>
          <comparator :view="viewObj" :compare="compareObj"
            prop="speech.delaytime">
            <template v-slot="{ value, compare, status }">
              <el-form-item :class="'comparable-' + status"
                label="Delay time">
                <el-input-number clearable
                  controls-position="right"
                  :min="5" :step="5"
                  v-model="value.speech.delaytime"
                  :disabled="compare">
                </el-input-number>
              </el-form-item>
            </template>
          </comparator>
          <comparator :view="viewObj" :compare="compareObj"
            prop="speech.volume">
            <template v-slot="{ value, compare, status }">
              <el-form-item :class="'comparable-' + status"
                label="Volume">
                <el-input-number clearable
                  controls-position="right"
                  :min="0" :max="100" :step="5"
                  v-model="value.speech.volume"
                  :disabled="compare">
                </el-input-number>
              </el-form-item>
            </template>
          </comparator>
        </div>
      </el-collapse-transition>
    </el-collapsable>
    <!-- follow intent inputs -->
    <comparator :view="viewObj" :compare="compareObj"
      :vuedata="{ button: {} }">
      <template v-slot="{ value, other, compare, vuedata }">
        <el-collapse-transition>
          <el-form-errors v-if="action === 'intent'">
            <el-button-group style="width: 100%">
              <!-- button to open the follow intent variables editor -->
              <el-button style="padding: 12px" type="info"
                :icon="hasVariables(value.intent.skill, value.intent.intent) && vuedata.button.icon || 'el-icon-minus'"
                :disabled="!hasVariables(value.intent.skill, value.intent.intent) || vuedata.button.disabled"
                @click.stop="$emit('activate', vuedata.button.next)"
                :tabindex="-1">
              </el-button>
              <!-- follow skill input -->
              <comparable prop="intent.skill" :default="null">
                <template v-slot="{ view, compare, status }">
                  <el-form-small-item :class="'comparable-' + status"
                    :prop="view && prop && prop + '.skill'">
                    <el-filterable-select
                      v-model="value.intent.skill"
                      :disabled="compare || intentDisabled"
                      :options="computedSkillOptions"
                      label-field="name">
                    </el-filterable-select>
                  </el-form-small-item>
                </template>
              </comparable>
              <!-- follow intent input -->
              <comparable prop="intent.intent" :default="null"
                :required="!intentDisabled">
                <template v-slot="{ view, compare, attrs, status }">
                  <el-form-small-item :class="'comparable-' + status"
                    label="Intent"
                    :prop="view && prop && prop + '.intent'"
                    v-bind="attrs"
                    field="intent">
                    <el-filterable-select
                      v-model="value.intent.intent"
                      :disabled="compare || intentDisabled"
                      :placeholder="intentDisabled ? '' : 'Choose an intent'"
                      :options="getIntentOptions(value.intent.skill)"
                      label-field="name">
                      <template slot="option" slot-scope="{item}">
                        <span v-if="item.context"
                          class="follow-intent-editor__prefix"
                          >[{{item.contextName}}] </span>
                        <span class="el-filterable-select__left"
                          >{{ item.left }}</span>
                        <strong class="el-filterable-select__match"
                          >{{ item.match }}</strong>
                        <span class="el-filterable-select__right"
                          >{{ item.right }}</span>
                      </template>
                    </el-filterable-select>
                  </el-form-small-item>
                </template>
              </comparable>
              <!-- a link to visit the follow intent -->
              <router-link v-if="!value.intent.skill && value.intent.intent"
                style="padding: 12px"
                :to="{name: 'intent', params: {skill: $route.params.skill, intent: value.intent.intent}, query: $route.query}"
                target="_blank"
                class="el-button"
                :tabindex="-1">
                <i class="el-icon-link"></i>
              </router-link>
              <el-button v-else disabled
                style="padding: 12px"
                :tabindex="-1">
                <i class="el-icon-link"></i>
              </el-button>
            </el-button-group>
          </el-form-errors>
        </el-collapse-transition>
        <!-- an alert for the usage of a callback -->
        <el-collapse-transition>
          <el-alert
            v-show="action === 'callback'"
            type="warning"
            show-icon
            :closable="false">
            <template slot="title"><strong>[Advanced usage]</strong> Calling the <em>callback</em> is only useful if the <em>intent</em> is called with a <em>callback</em></template>
          </el-alert>
        </el-collapse-transition>
        <comparator-filter v-if="hasVariables(value.intent.skill, value.intent.intent)"
          @input="v => $emit('update:variables', v)">
          <template v-slot="{ listeners, view, compare, vuedata }">
            <!-- the follow intent variables editor -->
            <variables-editor
              :active="active"
              :forced="action === 'callback' || value.follow || other && other.follow"
              :closed="compare"
              @button="val => vuedata.button = val"
              :value="value.variables"
              :other="other && other.variables"
              v-on="listeners"
              :prop="view && prop && prop + '.variables'"
              type="entity"
              :local-variables="localVariables"
              :context-variables="contextVariables"
              :variable-suggestions="getVariableSuggestions(value.intent.skill, value.intent.intent)"
              :variable-names="getVariables(value.intent.skill, value.intent.intent)"
              :variable-new="getVariables(value.intent.skill, value.intent.intent) ? false : true"
              label="Local variables">
              <comparable v-if="!isOlliSkill(value.intent.skill)"
                prop="follow" diff :default="false"
                @input="v => $emit('update:follow', v)">
                <template v-slot="{ compare, listeners, status }">
                  <!-- follow checkbox -->
                  <el-checkbox :class="'comparable-' + status"
                    :value="value.follow"
                    v-on="listeners"
                    :disabled="compare">Follow all local variables
                    <tooltip-icon placement="top">
                      Follows all the <code>@var</code> local variables to the target <em>intent</em>.
                    </tooltip-icon>
                  </el-checkbox>
                </template>
              </comparable>
            </variables-editor>
          </template>
        </comparator-filter>
      </template>
    </comparator>
    <el-collapse-transition>
      <comparator v-if="!callbackDisabled"
        :view="viewObj" :compare="compareObj"
        :vuedata="{ button: {} }">
        <template v-slot="{ value, other, vuedata }">
          <p>
            <el-collapsable
              v-model="advanced"
              :forced="!!(hasSkill(value.intent.skill) || hasCallback(value.callback))">
              <!-- various alert for advanced usage -->
              <el-collapse-transition>
                <el-alert
                  v-show="hasSkill(value.intent.skill) && !hasCallback(value.callback)"
                  type="warning"
                  show-icon
                  :closable="false">
                  <template slot="title">If you don't define any <em>callback</em>, the user cannot return to the <em>skill</em></template>
                </el-alert>
              </el-collapse-transition>
              <el-collapse-transition>
                <el-alert
                  v-show="!hasSkill(value.intent.skill) && hasCallback(value.callback)"
                  type="warning"
                  show-icon
                  :closable="false">
                  <template slot="title"><strong>[Advanced usage]</strong> The target <em>intent</em> (or another <em>intent</em> further in the interaction) needs to call the <em>callback</em></template>
                </el-alert>
              </el-collapse-transition>
              <el-form-errors>
                <el-button-group style="width: 100%">
                  <!-- button to open the callback intent variables editor -->
                  <el-button style="padding: 12px" type="info"
                    :icon="hasCallback(value.callback) && vuedata.button.icon || 'el-icon-minus'"
                    :disabled="!hasCallback(value.callback) || vuedata.button.disabled"
                    @click.stop="$emit('activate', vuedata.button.next)"
                    :tabindex="-1">
                  </el-button>
                  <!-- a checkbox to activate the callback -->
                  <comparable prop="callback" diff :default="false"
                    @input="v => $emit('update:callback', v)">
                    <template v-slot="{ compare, listeners, status }">
                      <el-checkbox border
                        :class="'comparable-' + status"
                        @click.native.stop
                        :value="value.callback"
                        v-on="listeners"
                        :disabled="compare || hasOlliSkill">
                        Callback
                        <tooltip-icon placement="bottom"
                          :type="hasSkill(value.intent.skill) && !hasCallback(value.callback) || !hasSkill(value.intent.skill) && hasCallback(value.callback) ? 'warning' : 'info'">
                          <span v-if="!hasSkill(value.intent.skill)"
                            class="warning-text">
                            <i class="el-icon-warning"></i>[Advanced usage]<br/>
                          </span>
                          The <em>intent</em> to call after the skill has finished.<br/>
                          If checked, leave empty to just come back to the skill.
                          <template v-if="!hasSkill(value.intent.skill)"><br/>
                            Make sure the <em>intent</em> will call the <em>callback</em> enventually.
                          </template>
                        </tooltip-icon>
                      </el-checkbox>
                    </template>
                  </comparable>
                  <!-- the callback intent -->
                  <comparable prop="callbackCopy.intent" :default="null">
                    <template v-slot="{ view, compare, status }">
                      <el-form-small-item :class="'comparable-' + status"
                        label="Intent"
                        :prop="view && hasCallback(value.callback) && prop && prop + '.callback.intent' || undefined">
                        <el-filterable-select :clearable="!value.callbackCopy.skill"
                          v-model="value.callbackCopy.intent"
                          :placeholder="!hasCallback(value.callback) ? 'No callback' : value.callbackCopy.skill ? 'Choose a callback intent' : ''"
                          :disabled="compare || !hasCallback(value.callback)"
                          :options="callbackIntentOptions(value.callback)"
                          label-field="name">
                          <template slot="option" slot-scope="{item}">
                            <span v-if="item.context"
                              class="follow-intent-editor__prefix"
                              >[{{item.contextName}}] </span>
                            <span class="el-filterable-select__left"
                              >{{ item.left }}</span>
                            <strong class="el-filterable-select__match"
                              >{{ item.match }}</strong>
                            <span class="el-filterable-select__right"
                              >{{ item.right }}</span>
                          </template>
                        </el-filterable-select>
                      </el-form-small-item>
                    </template>
                  </comparable>
                </el-button-group>
              </el-form-errors>
              <!-- the callaback intent variables editor -->
              <el-collapse-transition>
                <comparator-filter v-if="hasCallback(value.callback)"
                  @valid="val => callbackValid = val"
                  @input="v => $emit('update:callbackVariables', v)">
                  <template v-slot="{ listeners, view, compare, vuedata }">
                    <variables-editor
                      :active="active"
                      :forced="value.callbackCopy.follow || other && other.callbackCopy.follow"
                      :closed="compare"
                      @button="val => vuedata.button = val"
                      :value="value.callbackVariables"
                      :other="other && other.callbackVariables"
                      v-on="listeners"
                      :prop="prop && prop + '.callback.variables'"
                      type="entity"
                      :local-variables="localVariables"
                      :context-variables="contextVariables"
                      :variable-suggestions="callbackVariableSuggestions(value.callback, value.callbackCopy.intent)"
                      label="Local variables">
                      <!-- a checkbox for local variables follow -->
                      <comparable prop="callbackCopy.follow" diff :default="false">
                        <template v-slot="{ compare, status }">
                          <el-checkbox :class="'comparable-' + status"
                            v-model="value.callbackCopy.follow"
                            :disabled="compare"
                            >Follow all local variables
                            <tooltip-icon placement="top">
                              Follows all the <code>@var</code> local variables to the callback <em>intent</em>.
                            </tooltip-icon>
                          </el-checkbox>
                        </template>
                      </comparable>
                    </variables-editor>
                  </template>
                </comparator-filter>
              </el-collapse-transition>
            </el-collapsable>
          </p>
        </template>
      </comparator>
    </el-collapse-transition>
  </div>
</template>

<script>
import ElFilterableSelect from '../element/ElFilterableSelect.vue'
import VariablesEditor from './VariablesEditor.vue'
import ElCollapsable from '../element/ElCollapsable.vue'

export default {
  name: 'FollowIntentSubEditor',

  components: {
    ElFilterableSelect,
    VariablesEditor,
    ElCollapsable,
  },

  props: {
    value: Object, // the value from the comparator
    other: Object, // the other value from the comparator
    compare: Boolean, // if showing compare (not view)
    action: String, // the follow action
    prop: String, // the prop prefix for el-form-item
    active: Boolean, // if should open the slidedowns

    specialSkills: Object, // the available special skills
    skillOptions: Array, // the available public skills
    intentOptions: Object, // the available intents, by skill
    // for the code-input
    localVariables: Array,
    contextVariables: Array,
    variableSuggestions: Object,
    // synced properties
    intent: Object, // the follow intent object
    follow: Boolean, // the follow local variables field
    variables: Object, // the follow intent variables
    callback: Boolean, // if callback is activated
    callbackCopy: Object, // the callback object
    callbackVariables: Object, // the callback intent variables
    speech: Object, // the speech object
  },

  data () {
    return {
      advanced: false,
    }
  },

  computed: {
    /* add "Same skill" and olli skills to the skills options */
    computedSkillOptions () {
      let out = [{ name: 'Same skill', value: null }]
      for (let skill in this.specialSkills) {
        out.push({ name: skill, value: skill })
      }
      return out.concat(this.skillOptions || [])
    },
    /* if editing view, use the synced props
    else just copy the value */
    viewObj () {
      return this.compare ? this.getObj(this.value) : {
        intent: this.intent,
        variables: this.variables,
        follow: this.follow,
        callback: this.callback,
        callbackCopy: this.callbackCopy,
        callbackVariables: this.callbackVariables,
        speech: this.speech,
      }
    },
    /* copy of the other */
    compareObj () {
      return this.getObj(this.other)
    },
    /* if cannot set an intent */
    intentDisabled () { return this.action !== 'intent' },
    /* if cannot set a callback */
    callbackDisabled () { return this.action !== 'intent' },
    /* if an OlliSkill has been selected */
    hasOlliSkill () {
      return this.isOlliSkill(this.value.skill)
    },
  },
  methods: {
    /* tell if a skill is an olli skill */
    isOlliSkill (skill) {
      return skill && skill.substr(0, 'olli.'.length) === 'olli.' || false
    },
    /* copies a value */
    getObj (value) {
      return value && {
        intent: {
          skill: value.skill || null,
          intent: value.intent || null,
        },
        variables: value.variables || {},
        follow: !!value.follow,
        callback: !!value.callback,
        callbackCopy: value.callback || {},
        callbackVariables: value.callback && value.callback.variables || {},
        speech: value.speech || {},
      }
    },
    /* if the variable editor is needed */
    hasVariables (skill, intent) {
      if (this.action === 'callback') return true
      else if (this.action !== 'intent') return false
      else if (!this.isOlliSkill(skill)) return true
      else return this.getVariables(skill, intent).length > 0
    },
    /* creates variable suggestion for not olli skill */
    getVariableSuggestions (skill, intent) {
      if (skill && intent && !this.isOlliSkill(skill)) {
        let suggestions = this.variableSuggestions[skill] || {}
        return suggestions[intent]
      }
      return undefined
    },
    /* get the prefilled list of variables for olli skills */
    getVariables (skill, intent) {
      if (this.isOlliSkill(skill)) {
        let olliIntent = this.specialSkills[skill]
          .find(x => x.intent === intent) || {}
        return olliIntent.slots || []
      }
      return undefined
    },
    /* get the available intents depending of the skill */
    getIntentOptions (skill) {
      if (this.isOlliSkill(skill)) {
        return this.specialSkills[skill].map(intent => {
          return { name: intent.intent, value: intent.intent }
        })
      } else {
        return this.intentOptions &&
          this.intentOptions[skill || '']
      }
    },
    /* if callback has been defined */
    hasCallback (callback) {
      return !this.callbackDisabled && callback
    },
    /* if the skill is not the local skill */
    hasSkill (skill) {
      return !this.intentDisabled && !!skill
    },
    /* the list of availble intents for callback,
    plus a "back to the skill" intent */
    callbackIntentOptions (callback) {
      if (!callback) return undefined
      return [{ name: 'Back to the skill', value: null }]
        .concat(this.intentOptions && this.intentOptions[''] || [])
    },
    /* suggestions for callback variables */
    callbackVariableSuggestions (callback, intent) {
      if (!callback || !intent) return undefined
      return (this.variableSuggestions[''] || {})[intent]
    },
  },
}
</script>
